import { PplUiIntl } from '../ppl-ui-intl';
import { NG_UI_THEMES, PIPELINER_NG_UI_THEME } from '../tokens';
import { PplVirtualScrollComponent } from '../virtual-scroll';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Inject,
  Input,
  Output,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { notFirstChange, trackById } from '@ppl/utils/angular.utils';
import {
  KEYCODE_DOWN,
  KEYCODE_END,
  KEYCODE_ENTER,
  KEYCODE_HOME,
  KEYCODE_PAGE_DOWN,
  KEYCODE_PAGE_UP,
  KEYCODE_UP
} from '@ppl/utils/keycode.utils';
import { MemoizeLast } from '@ppl/utils/memoize.utils';
import type {
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges
} from '@angular/core';

@Component({
  selector: 'ppl-option-list',
  templateUrl: './option-list.component.html',
  styleUrls: ['./option-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class PplOptionListComponent implements OnInit, OnChanges, OnDestroy {

  @Input() options: PplOptionListOption[];
  @Input() value: string;
  @Input() createValue?: string;
  @Input() maxContainerHeight?: number;
  @Input() displayCategoriesSidebar?: boolean;
  @Input() categoriesSidebarWidth?: number;
  @Input() listWidth?: number;
  @Input() selectedCategory?: string | null = null;

  @Input() actionsTemplate?: TemplateRef<any>;
  @Input() footerTemplate?: TemplateRef<any>;
  @Input() actionsTemplateHeight?: number;
  @Input() categories?: PplOptionListCategory[] | null;
  @Input() optionTemplate: TemplateRef<any>;
  @Input() optionTemplateRowHeight?: number;
  @Input() displayCreateOption?: string;
  @Input() displayLoading = false;
  @Input() displayNoResults = false;
  @Input() stopPropagation = true;
  @Input() displayAllCategories = false;
  @Input() preventKeys = false;
  @Input() disabled = false;
  @Input() @HostBinding('class.mark-selected') markSelected = true;

  @Output() valueChange = new EventEmitter<string>();
  @Output() optionSelect = new EventEmitter<PplOptionListOption>();
  @Output() optionCreate = new EventEmitter();
  @Output() listScrollEnd = new EventEmitter();
  @Output() categoryChange = new EventEmitter<string>();

  @ViewChild('virtualScroll', { read: ElementRef, static: false }) virtualScrollContainer: ElementRef;
  @ViewChild('virtualScroll', { read: PplVirtualScrollComponent, static: false }) virtualScroll: PplVirtualScrollComponent;

  preventAutoscroll = false;
  prevActiveIndex = -1;

  renderHandle = 0;
  rowHeights: number[];
  containerHeight: number;

  trackById = trackById;
  Math = Math;
  NormalizedOptionKind = NormalizedOptionKind;

  get showCategoriesSidebar() {
    return this.displayCategoriesSidebar && this.categories && this.categories.length > 1;
  }

  get displayCreate() {
    return this.displayCreateOption && this.createValue;
  }

  @MemoizeLast<PplOptionListComponent>(['options', 'displayLoading', 'selectedCategory'])
  get normalizedOptions(): NormalizedOption[] {
    const result: NormalizedOption[] = [];

    const filteredOptions = (this.selectedCategory === null)
      ? this.options
      : this.options.filter(option => option.categoryId === this.selectedCategory);

    for (let i = 0; i < filteredOptions.length; i++) {
      const option = filteredOptions[i];

      if (this.categories && !!option.categoryId && (i === 0 || (i !== 0 && option.categoryId !== filteredOptions[i - 1].categoryId))) {
        result.push({
          id: `__header__${option.categoryId}`,
          offsetTop: 0,
          kind: NormalizedOptionKind.Header,
          value: this.categories.find(category => category.id === option.categoryId) || null
        });
      }

      result.push({
        id: option.value,
        index: i,
        kind: NormalizedOptionKind.Item,
        value: option
      });
    }

    if (this.displayLoading) {
      result.push({
        id: '__loading__',
        kind: NormalizedOptionKind.Loading
      });
    }

    return result;
  }

  @MemoizeLast<PplOptionListComponent>(['normalizedOptions', 'rowHeights'])
  get normalizedOptionsCategoriesOnly() {
    const result: NormalizedOption[] = [];
    let elapsedHeight = 0;

    for (let i = 0; i < this.normalizedOptions.length; i++) {
      const option = this.normalizedOptions[i];

      if (option.kind === NormalizedOptionKind.Header) {
        result.push({
          ...option,
          offsetTop: elapsedHeight,
          kind: NormalizedOptionKind.Header,
        });
      }

      elapsedHeight += this.rowHeights[i];
    }

    return result;
  }

  @MemoizeLast<PplOptionListComponent>(['categories', 'options', 'selectedCategory'])
  get normalizedSearchCategories(): PplOptionListCategory[] {
    return [
      {
        id: null,
        label: this.intl.all
      },
      ...(this.categories || []).filter(category => {
        if (category.hiddenInSidebar) {
          return false;
        }
        if (!this.displayAllCategories && !this.options.some(option => option.categoryId === category.id)) {
          return false;
        }
        return true;
      })
    ];
  }

  constructor(
    @Inject(PIPELINER_NG_UI_THEME) public ngUiTheme: NG_UI_THEMES,
    public intl: PplUiIntl,
    private changeDetectorRef: ChangeDetectorRef
  ) { }

  ngOnChanges(changes: SimpleChanges) {
    if (notFirstChange(changes.options) || notFirstChange(changes.value)) {
      this.scrollToItem();
    }

    this.recalculateRowHeights();
  }

  ngOnInit() {
    this.scrollToItem();
  }

  ngOnDestroy() {
    cancelAnimationFrame(this.renderHandle);
  }

  onOptionClick(option: PplOptionListOption) {
    if (this.disabled) {
      return;
    }

    this.optionSelect.emit({ ...option });
  }

  onOptionCreate() {
    this.optionCreate.emit();
  }

  onOptionMouseEnter(value: string) {
    // Prevent autoscroll on mouse events (when item is only partially visible)
    this.preventAutoscroll = true;
    this.valueChange.emit(value);
    requestAnimationFrame(() => this.preventAutoscroll = false);
  }

  @HostListener('mousedown', ['$event'])
  onMouseDown(event: MouseEvent) {
    if (this.stopPropagation) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  onScrollChange(scroll: number) {
    const container = this.virtualScroll.recordsContainer.nativeElement.parentNode;

    if (Math.round(scroll) === container.scrollHeight - container.clientHeight) {
      this.listScrollEnd.emit();
    }
  }

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (this.preventKeys) {
      return;
    }

    const normalizedOptionsItemsOnly = this.normalizedOptions.filter(option => option.kind === NormalizedOptionKind.Item) as ItemNormalizedOption[];
    const activeIndex = normalizedOptionsItemsOnly.findIndex(option => option.value.value === this.value);
    const keyCode = event.keyCode;

    if (activeIndex !== -1) {
      switch (keyCode) {
        case KEYCODE_UP:
          this.valueChange.emit(normalizedOptionsItemsOnly[Math.max(activeIndex - 1, 0)].value.value);
          break;
        case KEYCODE_DOWN:
          this.valueChange.emit(normalizedOptionsItemsOnly[Math.min(activeIndex + 1, normalizedOptionsItemsOnly.length - 1)].value.value);
          break;
        case KEYCODE_PAGE_UP:
          this.valueChange.emit(normalizedOptionsItemsOnly[Math.max(activeIndex - ITEMS_SKIP - 1, 0)].value.value);
          break;
        case KEYCODE_PAGE_DOWN:
          this.valueChange.emit(normalizedOptionsItemsOnly[Math.min(activeIndex + ITEMS_SKIP + 1, normalizedOptionsItemsOnly.length - 1)].value.value);
          break;
        case KEYCODE_HOME:
          this.valueChange.emit(normalizedOptionsItemsOnly[0].value.value);
          break;
        case KEYCODE_END:
          this.valueChange.emit(normalizedOptionsItemsOnly[normalizedOptionsItemsOnly.length - 1].value.value);
          break;
        case KEYCODE_ENTER:
          // NOTE(mike): Added to prevent immediate submitting of form
          // when this component is inside form-group component and the form is valid
          event.preventDefault();
          const option = normalizedOptionsItemsOnly[activeIndex];
          this.optionSelect.emit(option.value);
          break;
      }
    } else if (keyCode === KEYCODE_ENTER && !this.options.length && this.displayCreate) {
      // enter triggers create if no options are available and displayCreate is on
      event.preventDefault();
      this.optionCreate.emit();
    }
  }

  onSelectedCategoryChange(categoryId: string) {
    this.selectedCategory = categoryId;

    const firstItem = this.normalizedOptions.find(option => option.kind === NormalizedOptionKind.Item) as ItemNormalizedOption;
    if (firstItem) {
      this.valueChange.emit(firstItem.value.value);
    }

    this.recalculateRowHeights();

    this.categoryChange.emit(this.selectedCategory);
  }

  private recalculateRowHeights() {
    const options = this.normalizedOptions;
    const rowHeight = this.optionTemplate ? this.optionTemplateRowHeight || BASIC_ROW_HEIGHT_PX : BASIC_ROW_HEIGHT_PX;

    this.rowHeights = options.map((option, i) => {
      if (option.kind === NormalizedOptionKind.Loading) {
        return (i === 0) ? rowHeight : LOADING_HEIGHT_PX;
      } else if (option.kind === NormalizedOptionKind.Header) {
        return (this.ngUiTheme === 'fabric' || this.ngUiTheme === 'material') ? CATEGORY_ROW_HEIGHT_PX_FABRIC_MATERIAL : CATEGORY_ROW_HEIGHT_PX;
      }

      return rowHeight;
    });

    let containerHeight = this.rowHeights.reduce((prev, next) => prev + next, 0);

    if (this.actionsTemplateHeight) {
      containerHeight += this.actionsTemplateHeight;
    }

    // stretch the "No results found" to full height
    if (!options.length && this.maxContainerHeight) {
      this.containerHeight = null;
    } else {
      this.containerHeight = Math.max(containerHeight, this.optionTemplateRowHeight || EMPTY_HEIGHT_PX);
    }

    this.changeDetectorRef.detectChanges();

    if (this.virtualScroll) {
      this.virtualScroll.forceUpdateLayout();
    }
  }

  private scrollToItem() {
    const activeIndex = this.normalizedOptions.findIndex(option => option.kind === NormalizedOptionKind.Item && option.value.value === this.value);

    if (activeIndex !== this.prevActiveIndex) {
      this.prevActiveIndex = activeIndex;

      if (!this.preventAutoscroll && this.virtualScroll && this.virtualScroll.recordsContainer) {
        const container = this.virtualScrollContainer.nativeElement as HTMLElement;

        if (activeIndex !== -1) {
          const item = this.virtualScroll.getRecordRect(activeIndex);
          const option = this.normalizedOptions[activeIndex] as ItemNormalizedOption;

          if (item) {
            const itemTop = item.top;
            const itemHeight = item.height;
            const categoryHeight = (option.value.categoryId ? CATEGORY_ROW_HEIGHT_PX : 0);

            if (itemTop - ITEMS_BORDER - categoryHeight < container.scrollTop) {
              container.scrollTop = itemTop - ITEMS_BORDER * 3 - categoryHeight;
            } else if (itemTop + itemHeight + ITEMS_BORDER > container.scrollTop + container.clientHeight) {
              container.scrollTop = itemTop - container.clientHeight + itemHeight - ITEMS_BORDER;
            }
          }
        } else {
          container.scrollTop = 0;
        }
      }
    }
  }
}

const ITEMS_SKIP = 5;
const ITEMS_BORDER = 1;
const BASIC_ROW_HEIGHT_PX = 25;
const CATEGORY_ROW_HEIGHT_PX = 26;
const CATEGORY_ROW_HEIGHT_PX_FABRIC_MATERIAL = 30;
const LOADING_HEIGHT_PX = 27;
const EMPTY_HEIGHT_PX = 25;

interface HeaderNormalizedOption {
  kind: NormalizedOptionKind.Header;
  id: string;
  offsetTop: number;
  value: PplOptionListCategory | null;
}

interface ItemNormalizedOption {
  kind: NormalizedOptionKind.Item;
  id: string;
  index: number;
  value: PplOptionListOption;
}

interface LoadingNormalizedOption {
  kind: NormalizedOptionKind.Loading;
  id: string;
}

type NormalizedOption = HeaderNormalizedOption | ItemNormalizedOption | LoadingNormalizedOption;

enum NormalizedOptionKind {
  Header = 'Header',
  Item = 'Item',
  Loading = 'Loading'
}

export interface PplOptionListCategory {
  id: string;
  label: string;
  hiddenInSidebar?: boolean;
}

export interface PplOptionListOption<T = any> {
  icon?: string;
  label: string;
  labelInList?: string;
  value: string;
  categoryId?: string;
  deleted?: boolean;
  disabled?: boolean;
  data?: T;
  color?: string;
}
